import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  ConfigurationEntry,
  ConfigurationHighlight,
  ConfigurationInclusion,
  INCLUSION_CATEGORY_LABELS,
  InclusionCategory,
  TourOfferConfiguration,
  TourOfferConfigurationContent,
} from '~/types/tours/tourOffer';

interface Props {
  configuration: TourOfferConfiguration;
  value: TourOfferConfigurationContent;
  onChange: (value: TourOfferConfigurationContent) => void;
  isChanged: boolean;
  defaultExpanded: boolean;
}

const MEAL_OPTIONS = ['Breakfast', 'Lunch', 'Dinner'];
// Itinerary text is a list of paragraphs; one blank line separates them in the field.
const PARAGRAPH_BREAK = '\n\n';

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" display="block">
      {children}
    </Typography>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip title="Remove">
      <IconButton aria-label={label} onClick={onClick}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** Whether every highlight, inclusion and itinerary day has what svc-tour requires. */
export function isConfigurationContentValid(value: TourOfferConfigurationContent): boolean {
  return (
    value.inclusions.every(
      (entry) => !!entry.title.trim() && (entry.type !== 'highlight' || !!entry.description.trim()),
    ) && value.itineraries.every((day) => !!day.title.trim())
  );
}

/** Drops the empty paragraphs the text field leaves while typing. */
export function cleanConfigurationContent(value: TourOfferConfigurationContent): TourOfferConfigurationContent {
  return {
    ...value,
    itineraries: value.itineraries.map((day) => ({ ...day, text: day.text.filter((paragraph) => paragraph.trim()) })),
  };
}

/** One configuration's copy of the base tour: about, highlights, inclusions and itinerary. */
export default function ConfigurationEditor({ configuration, value, onChange, isChanged, defaultExpanded }: Props) {
  const highlights = value.inclusions.filter((entry): entry is ConfigurationHighlight => entry.type === 'highlight');
  const inclusions = value.inclusions.filter((entry): entry is ConfigurationInclusion => entry.type === 'inclusion');
  const startDays = new Map(configuration.itineraries.map((day) => [day.id, day.startDay]));

  const set = (change: Partial<TourOfferConfigurationContent>) => onChange({ ...value, ...change });
  const updateEntry = (id: string, change: Partial<ConfigurationHighlight> | Partial<ConfigurationInclusion>) =>
    set({
      inclusions: value.inclusions.map((entry) =>
        entry.id === id ? ({ ...entry, ...change } as ConfigurationEntry) : entry,
      ),
    });
  const removeEntry = (id: string) => set({ inclusions: value.inclusions.filter((entry) => entry.id !== id) });
  const addEntry = (entry: ConfigurationEntry) => set({ inclusions: [...value.inclusions, entry] });
  const updateDay = (id: string, change: Partial<TourOfferConfigurationContent['itineraries'][number]>) =>
    set({ itineraries: value.itineraries.map((day) => (day.id === id ? { ...day, ...change } : day)) });

  return (
    <Accordion variant="outlined" disableGutters defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>
            <Typography variant="subtitle1">{value.title || configuration.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {configuration.name} · {highlights.length} highlights · {inclusions.length} inclusions ·{' '}
              {value.itineraries.length} itinerary days
            </Typography>
          </Box>
          {isChanged && <Chip size="small" color="primary" variant="outlined" label="Edited" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={value.title ?? ''}
              onChange={(event) => set({ title: event.target.value || null })}
              fullWidth
            />
            <TextField
              label="About"
              value={value.description ?? ''}
              onChange={(event) => set({ description: event.target.value || null })}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <SubHeading>Highlights</SubHeading>
            {highlights.map((entry) => (
              <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Stack spacing={1.5} flex={1}>
                    <TextField
                      size="small"
                      label="Highlight"
                      value={entry.title}
                      onChange={(event) => updateEntry(entry.id, { title: event.target.value })}
                      error={!entry.title.trim()}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Description"
                      value={entry.description}
                      onChange={(event) => updateEntry(entry.id, { description: event.target.value })}
                      error={!entry.description.trim()}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  </Stack>
                  <RemoveButton label="Remove highlight" onClick={() => removeEntry(entry.id)} />
                </Stack>
              </Paper>
            ))}
            <Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() => addEntry({ type: 'highlight', id: crypto.randomUUID(), title: '', description: '' })}
              >
                Add highlight
              </Button>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <SubHeading>What's included</SubHeading>
            {inclusions.map((entry) => (
              <Stack key={entry.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Category"
                  value={entry.category}
                  onChange={(event) => updateEntry(entry.id, { category: event.target.value as InclusionCategory })}
                  sx={{ minWidth: 220 }}
                >
                  {(Object.keys(INCLUSION_CATEGORY_LABELS) as Array<InclusionCategory>).map((category) => (
                    <MenuItem key={category} value={category}>
                      {INCLUSION_CATEGORY_LABELS[category]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Inclusion"
                  value={entry.title}
                  onChange={(event) => updateEntry(entry.id, { title: event.target.value })}
                  error={!entry.title.trim()}
                  fullWidth
                />
                <RemoveButton label="Remove inclusion" onClick={() => removeEntry(entry.id)} />
              </Stack>
            ))}
            <Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() =>
                  addEntry({
                    type: 'inclusion',
                    id: crypto.randomUUID(),
                    title: '',
                    category: 'other',
                    kind: 'standard',
                  })
                }
              >
                Add inclusion
              </Button>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <SubHeading>Itinerary</SubHeading>
            {value.itineraries.length === 0 && <Typography color="text.secondary">No itinerary days.</Typography>}
            {value.itineraries.map((day) => (
              <Paper key={day.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Typography variant="subtitle2" sx={{ minWidth: 56 }}>
                      Day {startDays.get(day.id)}
                    </Typography>
                    <TextField
                      size="small"
                      label="Title"
                      value={day.title}
                      onChange={(event) => updateDay(day.id, { title: event.target.value })}
                      error={!day.title.trim()}
                      fullWidth
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label="Description"
                    value={day.text.join(PARAGRAPH_BREAK)}
                    onChange={(event) => updateDay(day.id, { text: event.target.value.split(PARAGRAPH_BREAK) })}
                    helperText="Leave a blank line between paragraphs"
                    multiline
                    minRows={3}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="Accommodation"
                      value={day.accommodation ?? ''}
                      onChange={(event) => updateDay(day.id, { accommodation: event.target.value || null })}
                      fullWidth
                    />
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={MEAL_OPTIONS}
                      value={day.meals}
                      onChange={(_event, meals) => updateDay(day.id, { meals })}
                      renderTags={(meals, getTagProps) =>
                        meals.map((meal, index) => (
                          <Chip {...getTagProps({ index })} key={meal} size="small" label={meal} />
                        ))
                      }
                      renderInput={(params) => <TextField {...params} label="Meals" />}
                      sx={{ minWidth: { sm: 280 } }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
